import { defaultReturn, defaultReturnNotToast } from '../../../util/newStudent';
import * as api from '../../api/newStudent/apiStudent.service';
import type {
  ICourses,
  ISubjects,
  IContentSubject,
  IContentSubjectGraduation,
  IListOfExams,
  IRulesToSend,
  IDataPillarTcc,
  IShowInternship,
  IAnswerQuestion,
  IExam,
} from '../../../types/newStudent/myCourses.interface';

export type {
  ICourse,
  ICourses,
  ISubjectContent,
  ISubject,
  ISubjects,
  IDataContentSubject,
  IContentSubjectGraduation,
  IContentSubjectGraduationClass,
  IContentSubject,
  IExamDescription,
  IListOfExams,
  IAlternative,
  IQuestion,
  IExam,
  IAnswers,
  IAnswerQuestion,
  IHistoric,
  IShowInternship,
  IRulesToSendTcc,
  IPillarTcc,
  IDataPillarTcc,
  IRulesToSend,
} from '../../../types/newStudent/myCourses.interface';

export const myCoursesServiceNewStudent = {
  async show(): Promise<{
    data: ICourses[];
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/show',
        'GET',
      );
      return defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async getPpc(courseId: number) {
    const request = await api.authenticatedRequest(
      '/student/my-courses/ppc',
      'POST',
      { courseId: courseId },
    );

    return defaultReturn(request);
  },

  async getDisciplines(id: number): Promise<{
    data: ISubjects[];
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/disciplines',
        'POST',
        { id: id },
      );
      return defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async getContent(
    id: number | string,
    disciplineId: number | string,
    contentId: number | string,
    quiet = false,
  ): Promise<{
    data: IContentSubject | IContentSubjectGraduation[];
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/content',
        'POST',
        { id, disciplineId, contentId },
      );
      return quiet ? defaultReturnNotToast(request) : defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async listExams(
    id: number,
    disciplineId: number,
    contentId: number,
    resourceId?: number | undefined,
    quiet = false,
  ): Promise<{
    data: IListOfExams;
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/exam/show-exams',
        'POST',
        {
          id,
          disciplineId,
          contentId,
          resourceId,
        },
      );
      return quiet ? defaultReturnNotToast(request) : defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async getExam(
    id: number,
    disciplineId: number,
    contentId: number,
    examId?: number,
    resourceId?: number,
  ): Promise<{
    data: IExam;
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/exam/show-exam',
        'POST',
        {
          id,
          disciplineId,
          contentId,
          examId,
          resourceId,
        },
      );
      return defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async getAnswerSheet(
    examId?: number,
    examType?: number,
  ): Promise<{
    data: IExam;
    message: string[];
    result: boolean;
  }> {
    try {
      const request = await api.authenticatedRequest(
        '/student/my-courses/exam/answers-list',
        'POST',
        {
          examId,
          examType,
        },
      );
      return defaultReturn(request);
    } catch (error) {
      throw new Error(error);
    }
  },

  async answerQuestion(data: IAnswerQuestion): Promise<{
    data: boolean;
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/exam/answer-question',
      'POST',
      data,
    );
    return request;
  },

  async finishExam(
    examId?: number | string,
    examType?: number | string,
  ): Promise<{
    data: boolean;
    message: string[];
    result: boolean;
  }> {
    if (!examId || examType === undefined || examType === null) {
      return {
        result: false,
        data: false,
        message: ['Dados da prova incompletos. Recarregue a página e tente novamente.'],
      };
    }

    const request = await api.authenticatedRequest(
      '/student/my-courses/exam/answer-exam',
      'POST',
      {
        examId,
        examType,
      },
    );

    if (!request) {
      return {
        result: false,
        data: false,
        message: ['Não foi possível finalizar a prova. Tente novamente.'],
      };
    }

    return defaultReturn(request);
  },

  async showAcademicProject(body: {
    id: string | number;
    disciplineId: string | number;
    contentId: string | number;
    resourceId: string | number;
    assessmentWork: boolean;
  }): Promise<{
    data: {
      dismissalTcc?: boolean;
      finalCorrectionTcc?: boolean;
      orientationTcc?: boolean;
      paymentType?: boolean;
      tccObservationDismissal?: string[];
      overview?: string[];
      project?: IShowInternship[];
    };
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/academic-projects/resource',
      'POST',
      body,
    );

    return request;
  },

  async saveNewHistoricInternship(data: FormData): Promise<{
    data: object | boolean;
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/academic-projects/new-register',
      'POST',
      data,
      'file',
    );

    return defaultReturn(request);
  },

  async tccPillar(body: {
    id: number;
    disciplineId: number;
    contentId: number;
    resourceId: number;
  }): Promise<{
    data: IDataPillarTcc;
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/academic-projects/pillar-resource',
      'POST',
      body,
    );
    return request;
  },

  async resourceRules({ resourceId, type }): Promise<{
    data: IRulesToSend;
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/academic-projects/resource-rules',
      'POST',
      { resourceId, type },
    );

    return defaultReturn(request);
  },

  async resourceHistoricAttachment({
    resourceId,
    academicProjectId,
    academicProjectHistoricId,
  }): Promise<{
    data: { name: string; url: string }[];
    result: boolean;
    message: string[];
  }> {
    const request = await api.authenticatedRequest(
      '/student/my-courses/academic-projects/resource-historic',
      'POST',
      { resourceId, academicProjectId, academicProjectHistoricId },
    );

    return defaultReturn(request);
  },

  async generateContedMaterial(contedId: string): Promise<{
    data: { url: string };
    message: string[];
    result: boolean;
  }> {
    const request = await api.authenticatedRequest(
      `/student/my-courses/generate-conted-material`,
      'POST',
      { contedId },
    );

    return defaultReturn(request);
  },
};
